import React, { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { Plus, Trash2, Save, X, AlertTriangle, Undo2 } from "lucide-react";
import { toast } from "sonner";
import { PageHeader, Spinner, Card, PrimaryButton, SecondaryButton, Input, Textarea, Select, EmptyState, Badge, Table, THead, TR, TH, TD } from "./_ui";

const TYPES = ["Avertissement", "Blâme", "Suspension", "Exclusion"];
const EMPTY = { member_name: "", member_email: "", sanction_type: "Avertissement", reason: "", decided_at: "", status: "active" };

export default function SanctionsPanel() {
  const [items, setItems] = useState(null);
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/admin/sanctions").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!form.member_name || !form.reason || !form.decided_at) return toast.error("Membre, motif et date requis");
    setBusy(true);
    try {
      await api.post("/admin/sanctions", {
        ...form,
        decided_at: new Date(form.decided_at).toISOString(),
      });
      toast.success("Sanction enregistrée");
      setAdding(false); setForm(EMPTY); await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
    finally { setBusy(false); }
  };

  const lift = async (id) => {
    if (!window.confirm("Lever cette sanction ?")) return;
    await api.put(`/admin/sanctions/${id}/lift`);
    toast.success("Sanction levée"); await load();
  };

  const remove = async (id) => {
    if (!window.confirm("Supprimer définitivement ?")) return;
    await api.delete(`/admin/sanctions/${id}`);
    toast.success("Supprimée"); await load();
  };

  if (!items) return (<><PageHeader title="Sanctions" /><Spinner /></>);

  const colorFor = (t) =>
    t === "Exclusion" ? "red" : t === "Suspension" ? "red" : t === "Blâme" ? "gold" : "slate";

  return (
    <div data-testid="admin-sanctions">
      <PageHeader
        title="Sanctions"
        subtitle="Registre disciplinaire interne — accès restreint aux administrateurs."
        action={
          !adding && (
            <PrimaryButton onClick={() => setAdding(true)} data-testid="sanc-create-btn">
              <Plus size={16} /> Nouvelle sanction
            </PrimaryButton>
          )
        }
      />

      {adding && (
        <Card className="p-5 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Input placeholder="Nom du membre" value={form.member_name} onChange={(e) => setForm({ ...form, member_name: e.target.value })} data-testid="sanc-member" />
            <Input type="email" placeholder="Email (facultatif)" value={form.member_email} onChange={(e) => setForm({ ...form, member_email: e.target.value })} />
            <Select value={form.sanction_type} onChange={(e) => setForm({ ...form, sanction_type: e.target.value })} data-testid="sanc-type">
              {TYPES.map((t) => <option key={t}>{t}</option>)}
            </Select>
            <Input type="date" value={form.decided_at} onChange={(e) => setForm({ ...form, decided_at: e.target.value })} data-testid="sanc-date" />
          </div>
          <Textarea placeholder="Motif & contexte" rows={3} value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} className="mt-3" data-testid="sanc-reason" />
          <div className="flex gap-3 mt-4">
            <PrimaryButton onClick={save} disabled={busy} data-testid="sanc-save"><Save size={16} /> Enregistrer</PrimaryButton>
            <SecondaryButton onClick={() => { setAdding(false); setForm(EMPTY); }}><X size={16} /> Annuler</SecondaryButton>
          </div>
        </Card>
      )}

      {items.length === 0 ? (
        <EmptyState icon={AlertTriangle} title="Aucune sanction enregistrée" hint="Le registre est vide — la discipline règne." />
      ) : (
        <Table>
          <THead>
            <tr><TH>Membre</TH><TH>Type</TH><TH>Date</TH><TH>Motif</TH><TH>Statut</TH><TH>Décidé par</TH><TH /></tr>
          </THead>
          <tbody>
            {items.map((s) => (
              <TR key={s.id} data-testid={`sanc-${s.id}`}>
                <TD className="text-white">{s.member_name}</TD>
                <TD><Badge color={colorFor(s.sanction_type)}>{s.sanction_type}</Badge></TD>
                <TD className="text-xs">{new Date(s.decided_at).toLocaleDateString("fr-FR")}</TD>
                <TD className="text-xs max-w-md truncate" title={s.reason}>{s.reason}</TD>
                <TD><Badge color={s.status === "active" ? "red" : "slate"}>{s.status === "active" ? "Active" : "Levée"}</Badge></TD>
                <TD className="text-xs text-slate-400">{s.decided_by}</TD>
                <TD>
                  <div className="flex items-center gap-2">
                    {s.status === "active" && (
                      <button onClick={() => lift(s.id)} className="text-xs text-emerald-400 hover:underline inline-flex items-center gap-1" data-testid={`sanc-lift-${s.id}`}>
                        <Undo2 size={11} /> Lever
                      </button>
                    )}
                    <button onClick={() => remove(s.id)} className="text-slate-500 hover:text-[#CE1126]"><Trash2 size={13} /></button>
                  </div>
                </TD>
              </TR>
            ))}
          </tbody>
        </Table>
      )}
    </div>
  );
}
